/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
// This rule need to be disabled because some value are falsy, but not only undefined or null
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { redirect } from 'next/navigation';
import { Order } from 'shared/types/cart';
import { getEnv } from '@/env';
import { Alert } from 'components/ui/atoms/alert';
import { PageLoader } from 'components/ui/atoms/loader';
import { OrderItemSummary } from 'components/ui/atoms/order-items-summary';
import { PaymentSummary } from 'components/ui/atoms/payment-summary';
import { SummaryDetails } from 'components/ui/atoms/summary-details';
import { SearchParams } from 'types/next';
import { formatDate } from 'utils';
import {
  centsToDollars,
  centsToDollarString,
  getPriceDetailsFromCart,
} from 'utils/price';
import { fetchCurrencyData } from './utils/currencyData';
import { fetchOrder } from './utils/fetchOrder';

import './order-details.scss';

type OrderDetailsTasticProps = {
  searchParams: SearchParams;
};

export const OrderDetailsTastic = ({
  searchParams,
}: OrderDetailsTasticProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] = useState<Order | null>(null);

  const orderNumber = searchParams.order_number;

  useEffect(() => {
    if (getEnv('NEXT_PUBLIC_ENABLE_DYNAMIC_CURRENCY') !== 'true') {
      redirect('/404');
    }

    setIsLoading(true);

    fetchOrder(orderNumber)
      .then((response) => {
        setOrder(response.data);
      })
      .catch(() => setOrder(null))
      .finally(() => setIsLoading(false));
  }, [orderNumber]);

  const priceDetails = getPriceDetailsFromCart(order ?? {});

  if (isLoading) {
    return <PageLoader loaderText="Fetching order..." />;
  }

  if (!order) {
    return (
      <Alert className="m-5" variant="danger">
        <FormattedMessage
          id="order.details.alert.message"
          defaultMessage="Something went wrong while fetching the order."
          description="order details alert message"
        />
      </Alert>
    );
  }

  const planned = order.payments?.at(0)?.amountPlanned;
  const currencyData = fetchCurrencyData(planned?.currencyCode ?? '');
  const paymentFractionDigits = planned?.fractionDigits ?? 2;
  const formattedPaymentAmount = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: paymentFractionDigits,
    maximumFractionDigits: paymentFractionDigits,
  }).format(
    centsToDollars({
      centAmount: planned?.centAmount ?? 0,
      fractionDigits: paymentFractionDigits,
    }),
  );

  return (
    <section className="order-wrapper">
      <section className="section-wrapper">
        <SummaryDetails
          orderNumber={orderNumber}
          createdAt={
            typeof order?.createdAt === 'string'
              ? formatDate(order.createdAt)
              : '-'
          }
          subTotal={centsToDollarString(
            priceDetails.lineItemsTotalPrice || 0,
            2,
          )}
          discount={{
            code: order?.discountCodes?.at(0)?.code,
            discountAmount: centsToDollarString(
              priceDetails?.cartDiscount?.amount || 0,
              2,
            ),
          }}
          total={centsToDollarString(priceDetails?.cartTotal || 0, 2)}
        />
      </section>

      <section className="section-wrapper">
        <OrderItemSummary lineItems={order?.lineItems || []} />
      </section>

      <section className="section-wrapper">
        <PaymentSummary
          currencyCode={currencyData?.code || 'USD'}
          totalAmount={formattedPaymentAmount}
          currencySymbol={currencyData?.symbol ?? '$'}
        />
      </section>
    </section>
  );
};
